/**
 * GroupRequestRejectionDialog Component
 *
 * Reusable dialog for rejecting group requests with a reason.
 * Pattern based on FAQs RejectionDialog.tsx
 *
 * Features:
 * - Text field for rejection reason (required)
 * - Validation - submit button disabled if reason is empty
 * - Loading state support
 * - Keyboard navigation support (Ctrl+Enter to submit)
 * - Helper text for user guidance
 *
 * Usage:
 * ```tsx
 * const [open, setOpen] = useState(false);
 *
 * <GroupRequestRejectionDialog
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   onConfirm={handleReject}
 *   requestId={request.id}
 * />
 * ```
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface GroupRequestRejectionDialogProps {
  /**
   * Whether dialog is open
   */
  open: boolean;
  /**
   * Close handler
   */
  onClose: () => void;
  /**
   * Confirm handler - receives rejection reason
   */
  onConfirm: (reason: string) => void;
  /**
   * Request ID (for logging/debugging)
   */
  requestId: string | null | undefined;
  /**
   * Whether rejection is being submitted
   */
  isSubmitting?: boolean;
}

export const GroupRequestRejectionDialog: React.FC<GroupRequestRejectionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  requestId,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const [rejectionReason, setRejectionReason] = useState('');

  // Reset reason when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setRejectionReason('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (rejectionReason.trim()) {
      onConfirm(rejectionReason);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && event.ctrlKey && rejectionReason.trim()) {
      handleConfirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="rejection-dialog-title"
    >
      <DialogTitle id="rejection-dialog-title">
        {t('groupRequests.approval.rejectDialog.title')}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t('groupRequests.approval.rejectDialog.reasonLabel')}
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          onKeyPress={handleKeyPress}
          helperText={t('groupRequests.approval.rejectDialog.reasonHelper')}
          disabled={isSubmitting}
          placeholder={t('groupRequests.approval.rejectDialog.reasonPlaceholder')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={isSubmitting || !rejectionReason.trim()}
        >
          {isSubmitting ? t('common.submitting') : t('groupRequests.approval.actions.reject')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
